// src/context/WorkspaceContext.jsx
/* eslint-disable react-refresh/only-export-components -- standard React
   Context colocation (Provider + useWorkspace() hook). */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { accountApi } from "../api/accountApi";
import { conductorApi } from "../api/conductorApi";
import { subscriptionApi } from "../api/subscriptionApi";
import { useAuth } from "./AuthContext";

const WorkspaceContext = createContext(null);

function readStoredAccountId() {
  const v = localStorage.getItem("current_account_id");
  return v ? Number(v) : null;
}

export function WorkspaceProvider({ children }) {
  const { isAuthenticated, permissionsLoading, setScopedPermissions, getScopedPermissions } =
    useAuth();

  const [accounts, setAccounts] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [currentAccountId, setCurrentAccountId] = useState(readStoredAccountId);
  const [currentEngagementId, setCurrentEngagementId] = useState(
    () => localStorage.getItem("current_engagement_id") || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Which modules the *selected project's own account* is actually
  // subscribed to — "conductor" only (never empty) until resolved, since
  // that one module is always free and every other module denies by
  // default with no account context (see CheckModuleAccessHandler's own
  // docstring on the backend). Used to hide, not just disable-on-click, a
  // module the current selection can't actually use.
  const [activeModules, setActiveModules] = useState(() => new Set(["conductor"]));

  // Only updates the selection itself (state + localStorage) — the actual
  // scoped-permissions computation is a separate reactive effect below, not
  // done inline here. Doing it inline used to mean: whichever engagement
  // syncAccountData()/refresh() resolves to on mount gets its scoped
  // permissions computed once, immediately, against whatever
  // AuthContext.permissionsMap happens to be *at that exact moment* — which
  // on a fresh page load/refresh is a real race against AuthContext's own
  // async permissions fetch. If that fetch hadn't resolved yet, this
  // captured an empty snapshot with nothing to ever recompute it, so every
  // permission-gated sidebar tab and ProtectedRoute check stayed wrong
  // until the user manually reselected the project (which called this
  // again, this time after the map had loaded). The effect below re-derives
  // scoped permissions from [permissionsLoading, currentEngagementId]
  // instead of a one-shot calculation, so it self-corrects the moment the
  // map actually finishes loading, with no user action required.
  const selectEngagement = useCallback((engagementId) => {
    setCurrentEngagementId(engagementId);
    if (engagementId == null) {
      localStorage.removeItem("current_engagement_id");
      return;
    }
    localStorage.setItem("current_engagement_id", engagementId);
  }, []);

  // True once scoped permissions have been computed at least once against a
  // fully-loaded permissionsMap — i.e. it's actually safe to judge a
  // project-scoped permission check. Starts false on every mount (a page
  // refresh included), so a consumer like ProtectedRoute can hold off
  // rendering/redirecting on it, the same way it already holds off on
  // AuthContext's own permissionsLoading. Without this, ProtectedRoute
  // could render in the one-render-cycle gap between permissionsLoading
  // flipping false and this file's own effect below actually running (effects
  // commit after render, not in the same pass) — hasPermission() would then
  // read the still-stale scopedPermissions from before this effect fires,
  // wrongly fail a project-scoped check, and <Navigate> to /unauthorized.
  // That redirect is a one-shot side effect: correcting scopedPermissions
  // one render later doesn't undo an already-committed navigation, which is
  // exactly why the sidebar (a passive re-render) self-healed but a
  // permission-gated route redirect did not.
  const [permissionsSynced, setPermissionsSynced] = useState(false);

  // Keeps scoped permissions in sync with [permissionsLoading,
  // currentEngagementId] — re-runs whenever either changes, including the
  // moment AuthContext's permissions fetch finishes after having already
  // started out stale/empty. See selectEngagement's own comment above for
  // why this can't just live inline in selectEngagement.
  useEffect(() => {
    if (permissionsLoading) return;
    setScopedPermissions(
      currentEngagementId != null
        ? getScopedPermissions({ projectId: currentEngagementId })
        : []
    );
    setPermissionsSynced(true);
  }, [permissionsLoading, currentEngagementId, getScopedPermissions, setScopedPermissions]);

  // Guards against a stale response overwriting a newer selection — see
  // syncAccountData's own check below.
  const syncSeqRef = useRef(0);

  // Fetches one account's engagements + active modules together, in
  // parallel, and applies both — modules are account-scoped, not
  // project-scoped, so this only ever needs to run on an account change,
  // never on a plain project switch. Reconciles the current project
  // selection against the freshly-scoped engagements list, same as before.
  const syncAccountData = useCallback(
    async (accountId) => {
      const requestId = ++syncSeqRef.current;
      const [engagementsResult, modulesResult] = await Promise.all([
        conductorApi.listEngagements(accountId).catch(() => ({ data: [] })),
        accountId != null
          ? subscriptionApi.myModules(accountId).catch(() => null)
          : Promise.resolve(null),
      ]);

      // A newer call already started (the user switched again) — discard
      // this response instead of overwriting state with stale data.
      if (requestId !== syncSeqRef.current) return;

      const nextEngagements = engagementsResult.data || [];
      setEngagements(nextEngagements);

      const resolvedEngagementId =
        currentEngagementId != null &&
        nextEngagements.some((e) => e.engagement_id === currentEngagementId)
          ? currentEngagementId
          : nextEngagements[0]?.engagement_id ?? null;
      await selectEngagement(resolvedEngagementId);

      if (accountId == null || !modulesResult) {
        // No account, or the modules call failed — the backend denies
        // every module but CONDUCTOR with no account context.
        setActiveModules(new Set(["conductor"]));
      } else {
        const active = (modulesResult.data?.modules || [])
          .filter((m) => m.active)
          .map((m) => m.name);
        setActiveModules(new Set(active));
      }
    },
    [currentEngagementId, selectEngagement]
  );

  const selectAccount = useCallback(
    (accountId) => {
      setCurrentAccountId(accountId);
      if (accountId == null) localStorage.removeItem("current_account_id");
      else localStorage.setItem("current_account_id", String(accountId));
      syncAccountData(accountId);
    },
    [syncAccountData]
  );

  // Fires the accounts fetch and the current account's engagements+modules
  // fetch together, using whatever account id is already in localStorage
  // as a best guess, instead of waiting for the accounts list to resolve
  // before even starting the other two — the guess is right in the common
  // case (a returning user who hasn't changed accounts), so this collapses
  // what used to be three sequential round trips into one overlapped batch.
  // If the guess turns out stale (an id no longer in the fetched accounts
  // list), re-fetches scoped to the corrected account — an extra round
  // trip, but only on that rare path.
  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setAccounts([]);
      setEngagements([]);
      setActiveModules(new Set(["conductor"]));
      return;
    }
    setLoading(true);
    try {
      const guessAccountId = currentAccountId;
      let accountsFailed = false;

      const [accountsResult] = await Promise.all([
        accountApi.list().catch(() => {
          accountsFailed = true;
          return { data: [] };
        }),
        syncAccountData(guessAccountId),
      ]);
      setError(accountsFailed ? "Couldn't load accounts." : null);

      const nextAccounts = accountsResult.data || [];
      setAccounts(nextAccounts);

      const resolvedAccountId =
        guessAccountId != null &&
        nextAccounts.some((a) => a.account_id === guessAccountId)
          ? guessAccountId
          : nextAccounts[0]?.account_id ?? null;
      setCurrentAccountId(resolvedAccountId);
      if (resolvedAccountId == null) localStorage.removeItem("current_account_id");
      else localStorage.setItem("current_account_id", String(resolvedAccountId));

      if (resolvedAccountId !== guessAccountId) {
        await syncAccountData(resolvedAccountId);
      }
    } finally {
      setLoading(false);
    }
    // Only re-run when auth state flips — currentAccountId is read here as
    // the initial guess, not to re-trigger this fetch on every selection
    // change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createAccount = useCallback(async (payload) => {
    const result = await accountApi.create(payload);
    await refresh();
    return result.data;
  }, [refresh]);

  const updateAccount = useCallback(async (accountId, payload) => {
    const result = await accountApi.update(accountId, payload);
    await refresh();
    return result.data;
  }, [refresh]);

  const deactivateAccount = useCallback(async (accountId) => {
    await accountApi.deactivate(accountId);
    await refresh();
  }, [refresh]);

  const activateAccount = useCallback(async (accountId) => {
    await accountApi.activate(accountId);
    await refresh();
  }, [refresh]);

  const createEngagement = useCallback(
    async (payload) => {
      const result = await conductorApi.createEngagement({
        ...payload,
        account_id: currentAccountId,
      });
      await syncAccountData(currentAccountId);
      selectEngagement(result.data.engagement_id);
      return result.data;
    },
    [currentAccountId, syncAccountData, selectEngagement]
  );

  const currentAccount = useMemo(
    () => accounts.find((a) => a.account_id === currentAccountId) || null,
    [accounts, currentAccountId]
  );
  const currentEngagement = useMemo(
    () => engagements.find((e) => e.engagement_id === currentEngagementId) || null,
    [engagements, currentEngagementId]
  );

  const value = useMemo(
    () => ({
      accounts,
      engagements,
      currentAccountId,
      currentEngagementId,
      currentAccount,
      currentEngagement,
      activeModules,
      selectAccount,
      selectEngagement,
      createAccount,
      updateAccount,
      deactivateAccount,
      activateAccount,
      createEngagement,
      refresh,
      loading,
      error,
      permissionsSynced,
    }),
    [
      accounts,
      engagements,
      currentAccountId,
      currentEngagementId,
      currentAccount,
      currentEngagement,
      activeModules,
      selectAccount,
      selectEngagement,
      createAccount,
      updateAccount,
      deactivateAccount,
      activateAccount,
      createEngagement,
      refresh,
      loading,
      error,
      permissionsSynced,
    ]
  );

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within an WorkspaceProvider");
  return ctx;
}
